import { create } from "zustand";
import {
  getScorecards,
  getSlippage,
  getAnalytics,
  downloadPlanReport
} from "@/services/HrService/PlanReportService";
import { registerStoreReset } from "@/store/storeResetUtils";
import { TaskStatus } from "@/store/Employee/DailyPlanStore";

/** A half-open range: `to` is EXCLUSIVE, matching the API. Only PeriodSelector
 *  converts between this and the inclusive dates a human reads. */
export interface Period {
  from: string;
  to: string;
  label: string;
}

export interface Scorecard {
  employee_id: string;
  name: string;
  email: string;
  role_name: string;
  dept_id: number | null;
  dept_name: string | null;
  manager_name: string | null;
  has_left: boolean;

  expected_days: number;
  planned_days: number;
  missed_plan_days: number;
  eod_days: number;
  forgot_eod_days: number;
  leave_days: number;
  late_days: number;
  retro_days: number;

  plan_compliance_rate: number | null;
  eod_compliance_rate: number | null;

  tasks_total: number;
  completed: number;
  not_completed: number;
  carried: number;
  dropped: number;
  still_open: number;
  resolved: number;
  completion_rate: number | null;

  avg_tasks_per_planned_day: number | null;
  max_carry_count: number;
  chronic_slip_tasks: number;
  avg_hours_excl_retro: number | null;
}

export interface PeriodReport {
  period: { from: string; to: string; timezone: string; today: string };
  summary: {
    employees: number;
    tasks_total: number;
    completed: number;
    resolved: number;
    completion_rate: number | null;
    plan_compliance_rate: number | null;
    eod_compliance_rate: number | null;
    expected_days: number;
    planned_days: number;
    chronic_slip_tasks: number;
    late_days: number;
    forgot_eod_days: number;
  };
  rows: Scorecard[];
}

export interface AgingTask {
  id: number;
  employee_id: string;
  employee_name: string;
  dept_name: string | null;
  title: string;
  description: string | null;
  status: TaskStatus;
  plan_date: string;
  origin_date: string | null;
  /** How many times it was PUSHED. Not days — a task can be picked up weeks later. */
  carry_count: number;
  /** Calendar days since first planned. A different number from carry_count. */
  age_days: number;
  incomplete_reason: string | null;
  is_stale: boolean;
}

export interface ReasonBucket {
  key: string;
  label: string;
  count: number;
  share: number;
  samples: string[];
}

export interface SlippageReport {
  period: { from: string; to: string; today: string };
  agingTasks: AgingTask[];
  chronicSlippers: {
    employee_id: string;
    employee_name: string;
    dept_name: string | null;
    chronic_tasks: number;
    worst_carry_count: number;
  }[];
  staleOpen: {
    id: number;
    employee_name: string;
    title: string;
    plan_date: string;
    age_days: number;
  }[];
  thresholds: { chronic_carry: number; stale_after_days: number };
  reasons: {
    incomplete_buckets: ReasonBucket[];
    drop_buckets: ReasonBucket[];
    incomplete_verbatim: { reason: string; count: number }[];
    drop_verbatim: { reason: string; count: number }[];
    total_incomplete: number;
    total_dropped: number;
  };
}

export interface AnalyticsReport {
  period: { from: string; to: string };
  granularity: 'day' | 'week' | 'month';
  trend: {
    bucket: string;
    tasks_total: number;
    completed: number;
    resolved: number;
    completion_rate: number | null;
  }[];
  byDepartment: RollupGroup[];
  byManager: RollupGroup[];
  workload: {
    employee_id: string;
    name: string;
    tasks_total: number;
    completed: number;
    completion_rate: number | null;
  }[];
  summary: { tasks_total: number; completed: number; completion_rate: number | null };
}

export interface RollupGroup {
  key: string;
  label: string;
  employees: number;
  tasks_total: number;
  completed: number;
  resolved: number;
  completion_rate: number | null;
  plan_compliance_rate: number | null;
}

interface Filters {
  from: string;
  to: string;
  deptId?: string | number;
  scope?: 'org' | 'team';
}

interface PlanReportStore {
  periodReport: PeriodReport | null;
  slippage: SlippageReport | null;
  analytics: AnalyticsReport | null;

  loading: boolean;
  exporting: string | null;
  error: string | null;

  fetchPeriodReport: (filters: Filters) => Promise<void>;
  fetchSlippage: (filters: Filters & { minCarry?: number }) => Promise<void>;
  fetchAnalytics: (
    filters: Filters & { granularity?: 'day' | 'week' | 'month' }
  ) => Promise<void>;
  exportReport: (
    report: 'board' | 'employees' | 'slippage',
    format: 'csv' | 'xlsx' | 'pdf',
    filters: Record<string, any>
  ) => Promise<boolean>;
  clearError: () => void;
  resetStore: () => void;
}

const usePlanReportStore = create<PlanReportStore>((set) => ({
  periodReport: null,
  slippage: null,
  analytics: null,
  loading: false,
  exporting: null,
  error: null,

  fetchPeriodReport: async (filters) => {
    try {
      set({ loading: true, error: null });
      const response = await getScorecards(filters);
      if (response.success && response.data) {
        set({ periodReport: response.data, loading: false });
      } else {
        set({ error: response.error?.message || 'Failed to load the report', loading: false });
      }
    } catch (error: any) {
      set({ error: error.message, loading: false });
    }
  },

  fetchSlippage: async (filters) => {
    try {
      set({ loading: true, error: null });
      const response = await getSlippage(filters);
      if (response.success && response.data) {
        set({ slippage: response.data, loading: false });
      } else {
        set({ error: response.error?.message || 'Failed to load slippage', loading: false });
      }
    } catch (error: any) {
      set({ error: error.message, loading: false });
    }
  },

  fetchAnalytics: async (filters) => {
    try {
      set({ loading: true, error: null });
      const response = await getAnalytics(filters);
      if (response.success && response.data) {
        set({ analytics: response.data, loading: false });
      } else {
        set({ error: response.error?.message || 'Failed to load analytics', loading: false });
      }
    } catch (error: any) {
      set({ error: error.message, loading: false });
    }
  },

  exportReport: async (report, format, filters) => {
    try {
      set({ exporting: format, error: null });
      const response = await downloadPlanReport(report, format, filters);
      set({ exporting: null });
      if (!response.success) {
        set({ error: response.error?.message || 'Export failed' });
        return false;
      }
      return true;
    } catch (error: any) {
      set({ error: error.message, exporting: null });
      return false;
    }
  },

  clearError: () => set({ error: null }),
  resetStore: () =>
    set({
      periodReport: null,
      slippage: null,
      analytics: null,
      loading: false,
      exporting: null,
      error: null
    })
}));

registerStoreReset('planReport', () => usePlanReportStore.getState().resetStore());

export default usePlanReportStore;
